import { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { Upload, Trash2, Copy, X, Search } from "lucide-react";
import { toast } from "sonner";

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/media").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (err) { toast.error(`Failed: ${f.name}`); }
    }
    toast.success("Uploaded");
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const remove = async (id) => {
    if (!confirm("Delete this file?")) return;
    await api.delete(`/admin/media/${id}`);
    toast.success("Deleted"); load();
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("URL copied");
  };

  const filtered = items.filter((i) => i.original_filename?.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-8">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black text-3xl" data-testid="admin-media-title">Media Library</h1>
          <p className="text-neutral-500 mt-1">{items.length} files</p>
        </div>
        <label className="btn-primary-eq text-sm cursor-pointer" data-testid="media-upload">
          <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload"}
          <input ref={fileRef} type="file" multiple hidden onChange={upload} accept="image/*,application/pdf" />
        </label>
      </div>

      <div className="relative mt-6 max-w-md">
        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files..."
               className="w-full pl-11 pr-4 py-3 rounded-full border border-neutral-200 bg-white focus:outline-none focus:border-sky-500" data-testid="media-search" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-6">
        {filtered.map((f) => {
          const publicUrl = f.external_url || `${API}/files/${f.id}`;
          const isImg = f.content_type?.startsWith("image/");
          return (
            <div key={f.id} className="group relative aspect-square rounded-2xl overflow-hidden bg-neutral-100 border border-neutral-200" data-testid={`media-${f.id}`}>
              {isImg ? (
                <img src={publicUrl} alt={f.original_filename} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center p-3 text-xs text-neutral-600">
                  <div className="text-3xl">📄</div>
                  <div className="mt-2 text-center truncate w-full">{f.original_filename}</div>
                </div>
              )}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                <div className="text-white text-xs truncate">{f.original_filename}</div>
                <div className="flex gap-1 mt-2">
                  <button onClick={() => copyUrl(publicUrl)} className="flex-1 p-1.5 bg-white/20 hover:bg-white/30 rounded text-white text-xs flex items-center justify-center gap-1"><Copy className="w-3 h-3" /> URL</button>
                  <button onClick={() => remove(f.id)} className="p-1.5 bg-red-500/80 hover:bg-red-500 rounded text-white"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="col-span-full text-center py-16 text-neutral-500">No files. Click Upload to add your first file.</div>}
      </div>
    </div>
  );
}
